<template>
  <div v-if="booking && allocation" class="grow">
    <div class="flex items-center gap-4">
      <div class="rounded-md bg-[#d9f8d2] px-2 py-1 font-medium">{{ t('handover') }}</div>
      <img :src="carProp(allocation.car, 'imageUrl') ?? ''" class="h-7" />
      <div>
        {{ allocation.car.vehicleType.make.name }}
        {{ allocation.car.vehicleType.model }}
      </div>
    </div>

    <Divider />

    <HandoverCarStatusForm
      v-if="allocation && booking"
      :allocation="allocation"
      :booking="booking"
      :disabled="!handoverInspectionReady"
      :is-saving="isSaving || isLoading"
      @on-submit="handleSubmit"
    />
  </div>
  <div v-else class="flex items-center justify-center py-10">
    <Spinner />
  </div>
</template>

<script lang="ts" setup>
import type { CarStatusFormValues } from '@/entities/cars/car.entity';
import { useBooking } from '@/queries/use-bookings';
import { useFrozenHandoverInspectionRequired } from '@/components/inspections/handover-inspection-required';
import { useRoute, useRouter } from 'vue-router';
import HandoverCarStatusForm from '../car-status/HandoverCarStatusForm.vue';
import { useUpdateCar } from '@/queries/use-cars';
import { computed } from 'vue';
import { useUpdateAllocation } from '@/queries/use-allocations.ts';
import Divider from '@/components/Divider.vue';
import { carProp } from '@/utils/car-prop';
import Spinner from '@/components/icons/Spinner.vue';
import { useI18n } from 'vue-i18n';
import { useConfirmMilageDeviation } from '@/hooks/use-confirm-milage-deviation';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const { data: booking } = useBooking(route.params.bookingId as string);
const allocation = computed(() => {
  return booking.value?.allocations.find((allocation) => allocation.id === route.query.swapToId);
});

const { required: handoverInspectionRequired, isReady: handoverInspectionReady } =
  useFrozenHandoverInspectionRequired(booking, allocation);
const { confirmHandover, isLoading } = useConfirmMilageDeviation();
const { isPending: isSavingAllocation, mutateAsync: updateAllocation } = useUpdateAllocation();
const { isPending: isSavingCar, mutateAsync: updateCar } = useUpdateCar();

const isSaving = computed(() => isSavingAllocation.value || isSavingCar.value);

const handleSubmit = async (formValues: CarStatusFormValues) => {
  if (!booking.value || !allocation.value || !handoverInspectionReady.value) return;
  const { comment, isConfirmed, reason } = await confirmHandover(
    allocation.value,
    formValues.milage,
  );
  if (!isConfirmed) {
    return;
  }
  await updateAllocation({
    id: allocation.value.id,
    allocation: {
      milageStart: formValues.milage,
      fuelLevelStart: formValues.fuelLevel,
      milageDeviationComment: comment,
      milageDeviationReason: reason,
    },
  });
  await updateCar({
    id: allocation.value.car.id,
    car: {
      fuelLevel: formValues.fuelLevel,
    },
  });
  await router.push({
    name: handoverInspectionRequired.value ? 'swapInspection' : 'swapProtocol',
    params: {
      bookingId: booking.value.id,
    },
    query: {
      swapFromId: route.query.swapFromId as string,
      swapToId: route.query.swapToId as string,
    },
  });
};
</script>

<i18n lang="json">
{
  "en": {
    "handover": "Handover"
  },
  "de": {
    "handover": "Übergabe"
  },
  "es": {
    "handover": "Entrega"
  }
}
</i18n>
